import { MobxValue } from 'src_common/common/mobx-utils/MobxValue';

class NextId {
    private counter: number = 1;

    get(): number {
        const currentId = this.counter;
        this.counter++;
        return currentId;
    }
}

type RefreshFnType = () => void;
type UnsubscribeFnType = () => void;

type CreateSubscription = (refresh: RefreshFnType) => UnsubscribeFnType;

interface DeactivationSubscriptionType {
    timer: NodeJS.Timeout,
    unsubscribe: UnsubscribeFnType,
}

class KeepAlive {
    private readonly createSubscription: () => UnsubscribeFnType;
    private keepAlive: number;
    private deactivation: DeactivationSubscriptionType | null;

    constructor(createSubscription: () => UnsubscribeFnType, keepAlive: number) {
        this.createSubscription = createSubscription;
        this.keepAlive = keepAlive;
        this.deactivation = null;
    }

    connect(): UnsubscribeFnType {
        if (this.deactivation === null) {
            return this.createSubscription();
        }

        clearTimeout(this.deactivation.timer);
        const unsubscribe = this.deactivation.unsubscribe;
        this.deactivation = null;
        return unsubscribe;
    }

    private startDeactivation(unsubscribe: UnsubscribeFnType): DeactivationSubscriptionType {
        const timer = setTimeout(() => {
            this.deactivation = null;
            unsubscribe();
        }, this.keepAlive);

        return {
            timer,
            unsubscribe
        };
    }

    dispose(unsubscribe: UnsubscribeFnType): void {
        if (this.deactivation !== null) {
            console.error('Shutdown currently in progress');
            return;
        }

        this.deactivation = this.startDeactivation(unsubscribe);
    }
}

class QuickQuit {
    constructor(
        private readonly createSubscription: () => UnsubscribeFnType,
    ) {}

    connect(): UnsubscribeFnType {
        return this.createSubscription();
    }

    dispose(unsubscribe: UnsubscribeFnType): void {
        unsubscribe();
    }
}

interface SubscriptionStrategy {
    connect: () => UnsubscribeFnType,
    dispose: (unsubscribe: UnsubscribeFnType) => void,
}

class ConnectWrapper<T> {
    private readonly keepAlive: SubscriptionStrategy;

    constructor(
        private label: string | null,
        keepAlive: number | null,
        createSubscription: () => UnsubscribeFnType,
    ) {
        if (keepAlive === null) {
            this.keepAlive = new QuickQuit(createSubscription);
        } else {
            this.keepAlive = new KeepAlive(createSubscription, keepAlive);
        }
    }

    connect(_mobxData: MobxValue<T>): UnsubscribeFnType {
        if (this.label !== null) {
            console.info(`${this.label} - connect`);
        }
        return this.keepAlive.connect();
    }

    dispose(unsubscribe: UnsubscribeFnType): void {
        if (this.label !== null) {
            console.info(`${this.label} - dispose`);
        }
        this.keepAlive.dispose(unsubscribe);
    }
}

interface ApiResponseWrapperParams<T> {
    label: string | null,
    initValue: T,
    getData: () => Promise<T>,
    createSubscription: CreateSubscription,
    keepAlive?: number,
}

export class ApiResponseWrapper<T> {
    private readonly label: string | null;
    private readonly nextId: NextId;
    private currentRequestId: number;
    private readonly getData: () => Promise<T>;

    private readonly dataInner: MobxValue<T>;

    constructor(params: ApiResponseWrapperParams<T>) {
        this.label = params.label;
        this.nextId = new NextId();
        this.currentRequestId = 0;
        this.getData = params.getData;

        const createSubscription = (): UnsubscribeFnType => {
            return params.createSubscription(this.refresh);
        };

        this.dataInner = MobxValue.create({
            initValue: params.initValue,
            connect: new ConnectWrapper(
                params.label,
                params.keepAlive ?? null,
                createSubscription
            ),
        });
    }

    private getDataWithRequestId = async (): Promise<[number, T]> => {
        const requestId = this.nextId.get();

        const resp = await this.getData();
        return [requestId, resp];
    };

    private refresh = (): void => {
        if (this.label !== null) {
            console.info(`${this.label} - refresh`);
        }

        (async (): Promise<void> => {
            const [requestId, data] = await this.getDataWithRequestId();

            if (requestId > this.currentRequestId) {
                this.currentRequestId = requestId;
                this.dataInner.setValue(data);
            }
        })().catch((error) => {
            console.error(error);
        });
    };

    get data(): T {
        return this.dataInner.getValue();
    }
}
